import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SavedSoulBond } from '@/types/soulBond';
import { WS_URL } from '@/config/api';

const SERVICE_TAG = '[SESSION]';

/**
 * Proof requirement for a roll request (TICKET-281c).
 * - `image`: still photo only
 * - `video`: short video clip only
 * - `either`: user choice (default)
 * - `none`: no proof uploaded; result still recognised locally so user sees value
 */
export type ProofMode = 'image' | 'video' | 'either' | 'none';

export interface Player {
  id: string;
  name: string;
  color: string;
  /** Platform identities linked to this player (e.g., ['discord', 'foundry']) */
  linkedPlatforms?: string[];
}

export interface RollRequest {
  requestId: string;
  playerId: string;
  dice: Array<{ type: string; count: number }>;
  context: string;
  flavor?: string;
  /** Timestamp (ms) when the roll request was originally created. Present on pending roll delivery. */
  createdAt?: number;
  /** Proof requirement: image | video | either | none (TICKET-281c, default 'either') */
  proofMode?: ProofMode;
}

interface SessionState {
  serverUrl: string;
  sessionCode: string;
  playerId: string | null;
  playerName: string | null;
  sessionId: string | null;
  players: Player[];
  connectionStatus: 'disconnected' | 'connecting' | 'connected';
  pendingRollRequest: RollRequest | null;
  /** Unix timestamp (ms) when the pending roll request expires. Null when no pending request. */
  pendingRollExpiresAt: number | null;
  lastError: string | null;

  setServerUrl: (url: string) => void;
  setSessionCode: (code: string) => void;
  setPlayer: (id: string, name: string) => void;
  setSessionId: (sessionId: string | null) => void;
  setPlayers: (players: Player[]) => void;
  setConnectionStatus: (status: 'disconnected' | 'connecting' | 'connected') => void;
  setPendingRollRequest: (request: RollRequest | null) => void;
  clearPendingRollRequest: () => void;
  setLastError: (error: string | null) => void;
  reset: () => void;
  loadFromSoulBond: (soulBond: SavedSoulBond) => void;
}

const initialState = {
  serverUrl: WS_URL,
  sessionCode: '',
  playerId: null,
  playerName: null,
  sessionId: null,
  players: [],
  connectionStatus: 'disconnected' as const,
  pendingRollRequest: null,
  pendingRollExpiresAt: null,
  lastError: null
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      ...initialState,

      setServerUrl: (url) => set({ serverUrl: url }),
      setSessionCode: (code) => set({ sessionCode: code }),
      setPlayer: (id, name) => set({ playerId: id, playerName: name }),
      setSessionId: (sessionId) => set({ sessionId }),
      setPlayers: (players) => set({ players }),
      setConnectionStatus: (status) => set({ connectionStatus: status }),
      setPendingRollRequest: (request) => set(
        request !== null
          ? { pendingRollRequest: request, pendingRollExpiresAt: Date.now() + 5 * 60 * 1000 }
          : { pendingRollRequest: null, pendingRollExpiresAt: null }
      ),
      clearPendingRollRequest: () => set({ pendingRollRequest: null, pendingRollExpiresAt: null }),
      setLastError: (error) => set({ lastError: error }),
      reset: () => set(initialState),
      loadFromSoulBond: (soulBond) => {
        console.info(`${SERVICE_TAG} Loading from soul bond`, {
          id: soulBond.id,
          name: soulBond.name
        });
        set({
          serverUrl: soulBond.realmGateway,
          sessionCode: soulBond.chamberCode,
          playerName: soulBond.trueName,
          // Reset ephemeral state
          sessionId: null,
          players: [],
          connectionStatus: 'disconnected',
          pendingRollRequest: null,
          pendingRollExpiresAt: null,
          lastError: null
        });
      }
    }),
    {
      name: 'dicecapture-session',
      partialize: (state) => ({
        serverUrl: state.serverUrl,
        sessionCode: state.sessionCode,
        playerId: state.playerId,
        playerName: state.playerName,
        pendingRollRequest: state.pendingRollRequest,
        pendingRollExpiresAt: state.pendingRollExpiresAt
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Clear any pending roll that has passed its TTL expiry (5 min from receipt)
        if (state.pendingRollExpiresAt !== null && Date.now() > state.pendingRollExpiresAt) {
          console.info(`${SERVICE_TAG} Rehydration: clearing expired pending roll request`);
          state.clearPendingRollRequest();
        }
      }
    }
  )
);
